import axios from 'axios';
import { getApiUrl } from './environment-utils';

const API_BASE = getApiUrl();

// Helper to make API requests with error handling and fallback
export const apiClient = {
  // ---------------- CLASSES ----------------
  async getClasses() {
    try {
      const res = await axios.get(`${API_BASE}/classes`);
      if (Array.isArray(res.data)) {
        localStorage.setItem('classes', JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      console.warn('[API] Could not fetch classes from server, using local data:', err.message);
    }
    const local = localStorage.getItem('classes');
    return local ? JSON.parse(local) : [];
  },

  async saveClasses(classes) {
    localStorage.setItem('classes', JSON.stringify(classes));
    window.dispatchEvent(new Event('storage'));
    try {
      const res = await axios.post(`${API_BASE}/classes`, classes);
      return res.data;
    } catch (err) {
      console.error('[API] Error syncing classes with server:', err.message);
    }
  },

  async deleteClass(id) {
    try {
      if (id && id !== 'undefined') {
        await axios.delete(`${API_BASE}/classes/${encodeURIComponent(id)}`);
      }
    } catch (err) {
      console.warn('[API] Error deleting class on server:', err.message);
    }
  },

  // ---------------- COMPETITIONS ----------------
  async getCompetitions() {
    try {
      const res = await axios.get(`${API_BASE}/competitions`);
      if (Array.isArray(res.data)) {
        localStorage.setItem('competitions', JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      console.warn('[API] Could not fetch competitions, using local:', err.message);
    }
    const local = localStorage.getItem('competitions');
    return local ? JSON.parse(local) : [];
  },

  async saveCompetitions(competitions) {
    localStorage.setItem('competitions', JSON.stringify(competitions));
    window.dispatchEvent(new Event('storage'));
    try {
      const res = await axios.post(`${API_BASE}/competitions`, competitions);
      return res.data;
    } catch (err) {
      console.error('[API] Error syncing competitions:', err.message);
    }
  },

  // ---------------- COMPETITION SCORES (GRID) ----------------
  async getCompetitionScores(classId) {
    try {
      const res = await axios.get(`${API_BASE}/competition-scores/${classId}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        localStorage.setItem(`competitionScores-${classId}`, JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      console.warn(`[API] Could not fetch scores for class ${classId}:`, err.message);
    }
    const local = localStorage.getItem(`competitionScores-${classId}`);
    return local ? JSON.parse(local) : [];
  },

  async saveCompetitionScores(classId, data) {
    localStorage.setItem(`competitionScores-${classId}`, JSON.stringify(data));
    try {
      await axios.post(`${API_BASE}/competition-scores/${classId}`, { data });
    } catch (err) {
      console.warn(`[API] Error saving scores for class ${classId}:`, err.message);
    }
  },

  // ---------------- WINNERS & REVEAL ----------------
  async getWinners() {
    try {
      const res = await axios.get(`${API_BASE}/winners`);
      if (res.data) {
        if (res.data.winners) {
          localStorage.setItem('winners', JSON.stringify(res.data.winners));
        }
        if (res.data.winnerRevealData) {
          localStorage.setItem('winnerRevealData', JSON.stringify(res.data.winnerRevealData));
        }
        return res.data;
      }
    } catch (err) {
      console.warn('[API] Could not fetch winners, using local:', err.message);
    }
    const localWinners = localStorage.getItem('winners');
    const localReveal = localStorage.getItem('winnerRevealData');
    return {
      winners: localWinners ? JSON.parse(localWinners) : [],
      winnerRevealData: localReveal ? JSON.parse(localReveal) : null,
    };
  },

  async saveWinners(winners, winnerRevealData) {
    if (winners !== undefined) {
      localStorage.setItem('winners', JSON.stringify(winners));
    }
    if (winnerRevealData !== undefined) {
      localStorage.setItem('winnerRevealData', JSON.stringify(winnerRevealData));
    }
    try {
      await axios.post(`${API_BASE}/winners`, { winners, winnerRevealData });
    } catch (err) {
      console.warn('[API] Error saving winners:', err.message);
    }
  },

  // ---------------- ALUMNI PHOTOS ----------------
  async getAlumniPhotos() {
    try {
      const res = await axios.get(`${API_BASE}/alumni`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        localStorage.setItem('alumniPhotos', JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      console.warn('[API] Could not fetch alumni photos:', err.message);
    }
    const local = localStorage.getItem('alumniPhotos');
    return local ? JSON.parse(local) : [];
  },

  async saveAlumniPhotos(photos) {
    localStorage.setItem('alumniPhotos', JSON.stringify(photos));
    try {
      await axios.post(`${API_BASE}/alumni`, { photos });
    } catch (err) {
      console.warn('[API] Error saving alumni photos:', err.message);
    }
  },

  // ---------------- VIDEOS / REELS ----------------
  async getVideos() {
    try {
      const res = await axios.get(`${API_BASE}/videos`);
      if (Array.isArray(res.data)) {
        localStorage.setItem('cached_videos', JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      console.warn('[API] Could not fetch videos from server:', err.message);
    }
    const local = localStorage.getItem('cached_videos');
    return local ? JSON.parse(local) : [];
  },

  async saveVideo(videoData) {
    try {
      const res = await axios.post(`${API_BASE}/videos`, videoData);
      return res.data;
    } catch (err) {
      console.error('[API] Error saving video to server:', err.message);
      throw err;
    }
  },

  async deleteVideo(id) {
    try {
      if (id && id !== 'undefined') {
        await axios.delete(`${API_BASE}/videos/${encodeURIComponent(id)}`);
      }
    } catch (err) {
      console.warn('[API] Error deleting video on server:', err.message);
    }
  },

  // ---------------- RECORD WINNERS ----------------
  async getRecordWinners() {
    try {
      const res = await axios.get(`${API_BASE}/records`);
      if (Array.isArray(res.data)) {
        localStorage.setItem('cached_records', JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      console.warn('[API] Could not fetch record winners from server:', err.message);
    }
    const local = localStorage.getItem('cached_records');
    return local ? JSON.parse(local) : [];
  },

  async saveRecordWinners(winners) {
    localStorage.setItem('cached_records', JSON.stringify(winners));
    try {
      const res = await axios.post(`${API_BASE}/records`, { winners });
      return res.data;
    } catch (err) {
      console.error('[API] Error saving record winners to server:', err.message);
    }
  },
};

export default apiClient;
